import React, { useContext, useMemo, useState } from 'react';
import axios from 'axios';
import _ from 'lodash';
import { planConstraintsParser } from 'parser/promoRecommendation';
import { tradePromoOptimizationUrls } from 'config/urls';

const errorMessage: string = 'Something went wrong!';

interface ConstraintDetails {
    max: number | null;
    min: number | null;
    value: number | null;
    originalMax: number | null;
    originalMin: number | null;
    originalValue: number | null;
}

interface Constraint {
    kpi: string;
    details: ConstraintDetails;
    isMinMax: boolean;
    objective: string;
    isMultiSelectKpi: boolean;
}

interface PlanConstraintsData {
    planId: number;
    constraints: Constraint[];
}

function useFetchConstraints(promoConstraints: any) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<PlanConstraintsData | null>(null);

    function onFetchComplete(payload: any) {
        try {
            if (payload && promoConstraints.length > 0) {
                const parsedData = planConstraintsParser(payload, promoConstraints);
                setData(parsedData as any);
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }

    function onFetchError(error: any = errorMessage) {
        setLoading(false);
        setData(null);
        setError(String(error));
    }

    const fetchPlanConstraints = async (planIds: number[]) => {
        setError(null);
        setLoading(true);
        setData(null);

        try {
            if (!Array.isArray(planIds) || planIds.length === 0) {
                return;
            }

            const planIdsString = [...planIds].sort().join(',');
            const response = await axios.get(
                `${tradePromoOptimizationUrls.getPlanConstraintsUrl}?plan_id=${planIdsString}`,
            );
            onFetchComplete(response.data);
        } catch (error: any) {
            onFetchError(error?.response?.data?.message || error?.message || errorMessage);
        }
    };

    return {
        loading,
        error,
        data,
        fetchPlanConstraints,
    };
}

export { useFetchConstraints };
